/**
 * 作り直しの変更の読取（API §4.0）— 案件の一覧・変更の履歴・案件の要求・案件の差分の入出力契約。
 *
 * 旧表を読んでいた 3 本（撤去済み）との差は、読む表が分かれることである（変更履歴 §3.3）。
 *
 * | procedure | 旧 | 作り直し |
 * |---|---|---|
 * | `listPjs` | 案件番号・状態・時刻 | **変更の行数と要求の件数**が付く（決定 16）。案件名（Backlog の課題の件名）も付く |
 * | `getChangeHistory` | 1 枚の変更ログ | 案件で引けば案件の変更、ノードか期間で引けば操作履歴。**理由の欄は無い** |
 * | `listPjRequirements` | 結ぶ相手は変更行の id | 結ぶ相手は**（案件, ノード）のノード** |
 * | `getPjDiff` | 無い | 案件の変更の行の前後を**欄ごと**に突き合わせた差分 |
 *
 * **差分の印の出どころは案件の変更の 1 本に絞る。** 旧は新規 / 更新 / 削除を変更ログの行から
 * 導いていたが、作り直しでは案件の変更の行の前後（`before` / `after`）だけが出どころになる。
 * 操作履歴は誰がいつ何をしたかの記録で、差分の印には使わない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 作り直しの案件の一覧・変更の履歴・案件の要求・案件の差分の入出力契約
 * @graph-connects none
 */
import { PJ_DIFF_CHANGE_KINDS, PJ_STATES, PJ_LANE_CHANGE_KINDS, PJ_LANE_NODE_TYPES, SPEC_OPERATION_KINDS, SPEC_PJ_CHANGE_STATUSES, } from '@cortex/domain-spec-graph';
import { z } from 'zod';
import { IsoDateTimeSchema, LocaleInputShape, NonEmptyStringSchema, PageInputShape, pageSchema, } from '../spec-graph-common.js';
import { NodeIdSchema, PjNumberSchema } from './pj-operations.js';
// 差分の欄の名前の読み方と出さない欄の判定は共通部の 1 つを画面へ渡す（画面で書き直さない）
export { isPjDiffHiddenField, parsePjDiffField, } from '@cortex/domain-spec-graph';
/**
 * 案件の状態。共通部の 2 値を写す。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の状態 2 値の出力スキーマ
 * @graph-connects none
 */
export const PjStateSchema = z.enum(PJ_STATES);
/**
 * 案件の一覧の 1 行。**返す件数は 2 つだけである**（決定 16）。
 *
 * 案件名は Backlog の課題の件名で、共通部の `listPjs` が課題の取り込み表から引く（決定 U21）。
 *
 * 使う画面の無い件数（承認・why・エッジ）は返さない。エッジの行はそもそも無くなる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の一覧の 1 行の出力スキーマ（案件番号・案件名・状態・最終更新・2 つの件数）
 * @graph-connects none
 */
export const PjRowSchema = z.object({
    pjNumber: PjNumberSchema,
    /** 案件名（Backlog の課題の件名）。課題が取り込まれていなければ null で、画面は「—」を出す */
    name: NonEmptyStringSchema.nullable(),
    state: PjStateSchema,
    updatedAt: IsoDateTimeSchema,
    /** 案件の変更の行数（案件 × ノードで 1 行） */
    changeCount: z.number().int().min(0),
    /** その案件が持つ要求の件数 */
    requirementCount: z.number().int().min(0),
});
/**
 * `listPjs` の入力。項目を 1 つも取らない。
 *
 * 空でも宣言するのは、契約側の「読取はすべて入力スキーマを持つ」を崩さないためである。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の一覧の入力スキーマ（絞り込みを持たない）
 * @graph-connects none
 */
export const ListPjsInputSchema = z.object({});
/**
 * `listPjs` の出力。期間の絞り込みを持たない。
 *
 * 期間を当てると、それより前に触られた未確定の案件が一覧から消える — 見落としてはいけない
 * ものが最初に消える絞り込みは置かない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の一覧の出力スキーマ
 * @graph-connects none
 */
export const ListPjsOutputSchema = z.object({
    pjs: z.array(PjRowSchema),
});
/**
 * 案件の変更 1 行。**理由の欄を持たない**（決定 3）。
 *
 * なぜ変えたかは、この行が結ばれた要求と why が持つ。`requirementIds` はその入口である。
 *
 * `before` / `after` は型を含んだノードの値（`{ nodeType, properties }`）で、BigQuery では
 * JSON 文字列の列である。読取では構造のまま返す — 画面が前後を並べて差分を描くためである。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の変更 1 行の出力スキーマ（案件 × ノードで 1 行、状態と結ばれた要求つき）
 * @graph-connects none
 */
export const PjChangeRowSchema = z.object({
    pjNumber: PjNumberSchema,
    nodeId: NodeIdSchema,
    before: z.record(z.string(), z.unknown()).nullable(),
    after: z.record(z.string(), z.unknown()).nullable(),
    status: z.enum(SPEC_PJ_CHANGE_STATUSES),
    updatedAt: IsoDateTimeSchema,
    actor: NonEmptyStringSchema,
    /** この（案件, ノード）が結ばれている要求の id */
    requirementIds: z.array(z.number().int().min(1)),
});
/**
 * 操作履歴 1 行。**誰がいつ何をしたかだけを持ち、状態を持たない。**
 *
 * `targetId` が何の id かは `kind` が決める（変更履歴 §3.2）。ノードの履歴を引くときは
 * 共通部が `kind` をノードへの操作の 4 値に絞るので、画面は `targetId` をノードの id として
 * 読んでよい。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 操作履歴 1 行の出力スキーマ（いつ・誰が・何を・打ち消した相手）
 * @graph-connects none
 */
export const OperationRowSchema = z.object({
    opId: z.number().int().min(1),
    at: IsoDateTimeSchema,
    actor: NonEmptyStringSchema,
    pjNumber: PjNumberSchema,
    kind: z.enum(SPEC_OPERATION_KINDS),
    targetId: z.number().int().min(1).nullable(),
    before: z.record(z.string(), z.unknown()).nullable(),
    after: z.record(z.string(), z.unknown()).nullable(),
    /** 打ち消した操作の opId。打ち消し以外は null */
    refOpId: z.number().int().min(1).nullable(),
});
/**
 * 変更の履歴の期間指定。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 操作履歴を期間で引くときの `from` / `to` の入力スキーマ
 * @graph-connects none
 */
export const ChangePeriodSchema = z.object({
    from: IsoDateTimeSchema,
    to: IsoDateTimeSchema,
});
/**
 * `getChangeHistory` の入力。**案件で引くか、ノードか期間で引くかで読む表が分かれる。**
 *
 * 3 つとも空の呼び出しは受け付けない（全件走査になる）。どれを渡したかで答える表が決まる
 * ので、入力そのものは今と同じ 3 つのままである。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 変更の履歴の入力スキーマ（案件・ノード・期間・ページ）
 * @graph-connects none
 */
export const GetChangeHistoryInputSchema = z
    .object({
    pjNumber: PjNumberSchema.optional(),
    nodeId: NodeIdSchema.optional(),
    period: ChangePeriodSchema.optional(),
    ...PageInputShape,
})
    .refine((input) => input.pjNumber !== undefined || input.nodeId !== undefined || input.period !== undefined, { message: '案件・ノード・期間のいずれか 1 つ以上を指すこと', path: ['pjNumber'] });
/**
 * 案件で変わったレーン 4 型（ステージ・業務フロー・業務・業務ルール）のノード 1 件。
 *
 * **配下の変更を含む。** 案件の変更の行はそのノード自身を書き換えたときにしか立たないので、
 * 業務ルールだけを足した案件では業務もステージも行を持たない。`changeKind` が `descendant` のノードは
 * 「自身は変わらないが、配下（業務フロー → 業務 → 業務ルール）のどれかが変わった」ことを表す。
 * 判定は共通部の `propagatePjLaneChanges` 1 箇所が持ち、画面は階層を辿らずにこれを読む。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件で変わったステージ・業務フロー・業務・業務ルール 1 件の出力スキーマ（id・型・直接の追加 / 変更 / 削除か配下に変更か）
 * @graph-connects none
 */
export const PjLaneChangeSchema = z.object({
    nodeId: NodeIdSchema,
    nodeType: z.enum(PJ_LANE_NODE_TYPES),
    changeKind: z.enum(PJ_LANE_CHANGE_KINDS),
});
/**
 * 案件で変わった画面 1 件。**部品の変更は持ち主の画面に寄せてある。**
 *
 * `changeKind` が `descendant` の画面は「自身は変わらないが、部品のどれかが変わった」ことを表す。
 * `businessIds`（部品が操作する業務）と `stageIds`（属するステージ）は画面の導出の関数で master と
 * 案件を当てた姿の両方から導いた和で、案件の変更一覧の画面のレーンを業務・ステージで絞るのに使う。
 * 判定は共通部の `buildPjScreenChanges` 1 箇所が持ち、画面は部品を寄せずにこれを読む。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件で変わった画面 1 件の出力スキーマ（id・変わり方・変わった部品・部品が操作する業務・属するステージ）
 * @graph-connects none
 */
export const PjScreenChangeSchema = z.object({
    screenId: NodeIdSchema,
    changeKind: z.enum(PJ_LANE_CHANGE_KINDS),
    componentIds: z.array(NodeIdSchema),
    businessIds: z.array(NodeIdSchema),
    stageIds: z.array(NodeIdSchema),
});
/**
 * `getChangeHistory` の出力。**読んだ表がそのまま形になる。**
 *
 * 案件で引いた答え（`changes`）とノード・期間で引いた答え（`operations`）を 1 つの配列に
 * 混ぜない。2 つは列も意味も違い、混ぜると画面が行ごとにどちらかを判定することになる。
 * 引かなかった側は空配列になる。
 *
 * `laneChanges` は案件で引いたときだけ中身を持ち、`changes` の行と階層のエッジから共通部が
 * 決めた「レーン 4 型のどれがどう変わったか」である。行に配下の伝播を混ぜないのは、行が
 * 案件の変更の表そのもの（案件 × ノードで 1 行）で、行の無いノードを行として返すと表の
 * 件数と食い違うためである。`screenChanges` も同じ理由で行と分け、部品の行を持ち主の画面に
 * 寄せたものを返す。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 変更の履歴の出力スキーマ（案件の変更と、そこから伝播したレーン 4 型の変更・持ち主に寄せた画面の変更、または操作履歴のページ）
 * @graph-connects none
 */
export const GetChangeHistoryOutputSchema = z.object({
    /** 案件で引いたときの答え。案件 × ノードで 1 行 */
    changes: z.array(PjChangeRowSchema),
    /** 案件で引いたときの、レーン 4 型の直接と配下の変更（id の昇順）。引かなければ空 */
    laneChanges: z.array(PjLaneChangeSchema),
    /** 案件で引いたときの、案件で変わった画面（部品は持ち主に寄せる。id の昇順）。引かなければ空 */
    screenChanges: z.array(PjScreenChangeSchema),
    /** ノードか期間で引いたときの答え */
    operations: pageSchema(OperationRowSchema),
});
/**
 * why が持つ根拠 1 件。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business why が持つ根拠 1 件（原典・観測日時・観測値）の出力スキーマ
 * @graph-connects none
 */
export const RequirementEvidenceSchema = z.object({
    source: NonEmptyStringSchema,
    /** その観測をした日時。書込日時ではない */
    observedAt: NonEmptyStringSchema,
    value: NonEmptyStringSchema,
});
/**
 * 要求が持つ why 1 件。
 *
 * `actor` はその why を立てた主体のメールアドレス。**空文字を拒まない** — 読取が 1 件の欠けで
 * 案件の要求の一覧ごと返せなくなるより、画面が「書いた人」を空として出すほうが読める。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 要求が持つ why 1 件（困りごと・変えたい状態と測り方・書いた主体・根拠）の出力スキーマ
 * @graph-connects none
 */
export const RequirementWhySchema = z.object({
    whyId: z.number().int().min(1),
    statement: NonEmptyStringSchema,
    outcome: NonEmptyStringSchema,
    actor: z.string(),
    evidences: z.array(RequirementEvidenceSchema),
});
/**
 * 案件が持つ要求 1 件。**結ぶ相手が（案件, ノード）のノードになる。**
 *
 * 「なぜ変えたか」の出どころはこの 1 本である（変更履歴 §3.3）。変更の行の `reason` は
 * 無くなるので、画面は変更のノードからこの一覧を引いて理由を出す。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件が持つ要求 1 件の出力スキーマ（文・受入条件・why・結ばれたノード）
 * @graph-connects none
 */
export const PjRequirementSchema = z.object({
    requirementId: z.number().int().min(1),
    statement: NonEmptyStringSchema,
    acceptance: NonEmptyStringSchema,
    whys: z.array(RequirementWhySchema),
    /** この要求を実現する案件の変更のノードの id */
    nodeIds: z.array(NodeIdSchema),
});
/**
 * `listPjRequirements` の入力。案件番号だけを取る。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の要求一覧の入力スキーマ
 * @graph-connects none
 */
export const ListPjRequirementsInputSchema = z.object({
    pjNumber: PjNumberSchema,
});
/**
 * `listPjRequirements` の出力。要求を立てた時刻の古い順に並ぶ。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の要求一覧の出力スキーマ
 * @graph-connects none
 */
export const ListPjRequirementsOutputSchema = z.object({
    requirements: z.array(PjRequirementSchema),
});
/**
 * 案件の差分の 1 欄。欄名と前後の値と、変わったか。
 *
 * その側に欄が無ければ値は null になる（新規の変更前・削除の変更後は全欄 null）。業務ルールの中身は
 * 判定表の行・式の段・並べ替えのキーの行・キーの決め方へ展開されて届く。欄の名前がどこを指すかは
 * `parsePjDiffField`（共通部）で読む。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の差分の 1 欄（欄名・変更前・変更後・変わったか）の出力スキーマ
 * @graph-connects none
 */
export const PjDiffFieldSchema = z.object({
    field: NonEmptyStringSchema,
    before: z.unknown(),
    after: z.unknown(),
    changed: z.boolean(),
});
/**
 * 案件の差分の 1 ノード。
 *
 * `label` は値の `label` を表示言語へ畳んだもので、`label` を持たない型（参照先の語から名前を
 * 組む型）と、その言語に本文が無いときは空文字になる。`changeKind` は行の前後の null だけから
 * 決まる（前が null なら追加、後が null なら削除）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の差分の 1 ノード（id・型・名前・追加 / 更新 / 削除・行の状態・欄の並び）の出力スキーマ
 * @graph-connects none
 */
export const PjDiffItemSchema = z.object({
    nodeId: NodeIdSchema,
    nodeType: z.string(),
    label: z.string(),
    changeKind: z.enum(PJ_DIFF_CHANGE_KINDS),
    status: z.enum(SPEC_PJ_CHANGE_STATUSES),
    fields: z.array(PjDiffFieldSchema),
});
/**
 * `getPjDiff` の入力。案件番号と表示言語を取る。
 *
 * 型や操作では絞らない。案件の変更は 1 案件で数十行に収まり、絞り込みは画面の中で済む — 契約に
 * 絞り込みを置くと、画面と MCP で絞り方が 2 通りになる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の差分の入力スキーマ（案件番号・表示言語）
 * @graph-connects none
 */
export const GetPjDiffInputSchema = z.object({
    pjNumber: PjNumberSchema,
    ...LocaleInputShape,
});
/**
 * 差分の値と欄の名前に現れる番号 1 つの名前（表示言語へ畳んだもの）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の差分に現れる要素の番号と、その表示名の対の出力スキーマ
 * @graph-connects none
 */
export const PjDiffLabelSchema = z.object({
    id: NodeIdSchema,
    label: z.string(),
});
/**
 * `getPjDiff` の出力。**`getChangeHistory(pjNumber)` と同じ行から組む。**
 *
 * 変更前は行の `before` である。未確定のあいだ master とずれていれば、それは土台ずれとして
 * 確定で止まる（変更履歴 §3.4）。`pjState` は行に 1 つでも `merged` があれば `merged` になる
 * （`listPjs` の状態の導出と同じ規則）。
 *
 * `labels` は、欄の値と欄の名前に現れる番号のうち要素を指すものの名前である。画面は値を JSON の
 * まま出さず、番号をこの名前へ置き換えて文字にする。業務の欄 `rules` は変更前 ／ 変更後それぞれ
 * 業務ルールの番号の順（業務ロジックの一覧と同じ）に並べて届く。`labels` を持たない応答
 * （欄を足す前の API）も受けられるよう任意にしてある。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の差分の出力スキーマ（案件番号・案件の状態・ノードごとの欄の差分・番号の名前）
 * @graph-connects none
 */
export const GetPjDiffOutputSchema = z.object({
    pjNumber: PjNumberSchema,
    pjState: PjStateSchema,
    items: z.array(PjDiffItemSchema),
    labels: z.array(PjDiffLabelSchema).optional(),
});
//# sourceMappingURL=pj-reads.js.map